<template>
    <ul class="accordion" role="tablist">
        <li v-for="(dataItem, i) in data" :key="i" class="accordion__item">
            <button
                class="accordion__button"
                :class="getStateClass(dataItem.id)"
                type="button"
                @click="toggle(dataItem.id)"
                @dragenter="onDragEnter(dataItem.id)"
                @dragleave="onDragLeave"
                @contextmenu.prevent="e => openContextMenu(e, dataItem)"
            >
                <figure v-if="dataItem.icon" class="accordion__icon">
                    <font-awesome-icon :icon="[dataItem.iconSet, dataItem.icon]" />
                </figure>

                <span class="accordion__text">
                    {{ dataItem.name | titleCase }}
                </span>

                <i class="accordion__arrow">
                    <font-awesome-icon :icon="['fal', menuArrow(dataItem.id)]" />
                </i>
            </button>

            <collapse
                :id="`collapse-${ dataItem.id }`"
                v-model="visibility[dataItem.id]"
                class="accordion__collapse"
                :accordion="`accordion-${ id }`"
                role="tabpanel"
            >
                <slot :data="dataItem.data" />
            </collapse>
        </li>
    </ul>
</template>

<script>
import { mapMutations, mapState } from 'vuex';
import uniqid from 'uniqid';

import { SHOW_MODAL } from '@/store/types/mutation-types';
import mapFilters from '@/utils/map-filters';
import listenOnRootMixin from '@/mixins/listen-on-root';
import { STATE_CLASSES } from '@/data/constants/classes';
import { COMPONENT_TREE_SECTIONS } from '@/data/constants/component-tree-sections';

export default {
    name: 'accordion',
    mixins: [listenOnRootMixin],

    props: {
        data: {
            type: Array,
            default: () => [],
        },

        visible: {
            type: String,
            default: '',
        },
    },

    data() {
        return {
            dragToggleTimeout: 0,
            id: uniqid(),
            isHovered: false,
            visibility: {},
        };
    },

    computed: {
        ...mapState({
            componentTree: state => state.page.componentTree,
        }),
    },

    watch: {
        visible(visibleId) {
            this.toggle(visibleId);
        },
    },

    created() {
        this.listenOnRoot('flex::accordion::show', this.show);
    },

    methods: {
        ...mapFilters(['paramCase']),

        ...mapMutations({
            showModal: SHOW_MODAL,
        }),

        menuArrow(id) {
            return this.visibility[id] ? 'chevron-up' : 'chevron-down';
        },

        show(id) {
            this.toggle(id, true);
        },

        getStateClass(id) {
            return {
                [STATE_CLASSES.SELECTED]: !!this.visibility[id],
                [STATE_CLASSES.HOVERED]: this.isHovered,
            };
        },

        toggle(id, forceToggle) {
            if (!this.visibility[id]) {
                this.$set(this.visibility, id, typeof forceToggle === 'boolean' ? forceToggle : false);
            }

            const isExpanded = typeof forceToggle === 'boolean' ? forceToggle : !this.visibility[id];
            const accordionEvent = isExpanded ? 'flex::accordion::expanded' : 'flex::accordion::collapsed';

            this.visibility[id] = isExpanded;
            this.$root.$emit(accordionEvent);
        },

        onDragEnter(id) {
            window.clearTimeout(this.dragToggleTimeout);

            this.dragToggleTimeout = window.setTimeout(() => {
                this.toggle(id, true);
            }, 500);

            this.isHovered = true;
        },

        onDragLeave() {
            window.clearTimeout(this.dragToggleTimeout);
            this.isHovered = false;
        },

        onAddFolder(data) {
            this.showModal({
                componentTag: 'add-folder-modal',
                data: {
                    id: data.id,
                },
            });
        },

        openContextMenu(event, dataItem) {
            const editableSectionNames = [COMPONENT_TREE_SECTIONS.CLIENT, COMPONENT_TREE_SECTIONS.SHARED];
            const sectionName = dataItem.name;

            if (!editableSectionNames.includes(sectionName) || dataItem.data.toolbar !== 'component') return;

            this.$root.$emit('flex::context::open', {
                event,
                id: 'component-list-context-menu',
                data: {
                    id: this.componentTree.find(treeItem => treeItem.name === sectionName).itemId,
                    isRoot: true,
                    isFolder: true,
                    onAddCallback: this.onAddFolder,
                },
            });
        },
    },
};
</script>

<style lang="scss">
.accordion {
    color: var(--accordion-color);
    background-color: var(--accordion-backgroundColor);
    border-bottom: 2px solid var(--navbar-tabSelectedBackgroundColor);
    padding: 0;
    margin: 0;

    &__item {
        list-style: none;

        &:not(:first-of-type) {
            border-top: 1px solid var(--accordion-barBorderColor1);
        }
    }

    &__button {
        display: flex;
        align-items: center;
        border: none;
        color: var(--accordion-barColor1);
        background-color: var(--accordion-barBackgroundColor1);
        width: 100%;
        padding: 5px 13px 5px 10px;

        &:hover,
        &.#{ $is-hovered } {
            color: var(--accordion-barHoverColor1);
            background-color: var(--accordion-barHoverBackgroundColor1);
        }

        &.#{ $is-selected } {
            color: var(--accordion-barSelectedColor1);
            background-color: var(--accordion-barSelectedBackgroundColor1);
        }
    }

    &__icon {
        width: 28px;
        margin: 0;
        font-size: 21px;
    }

    &__text {
        margin: 0 15px;
        font-size: 13px;
        font-weight: 600;
        pointer-events: none;
    }

    &__arrow {
        margin-left: auto;
        position: relative;
        top: 1px;
    }
}
</style>
